/* eslint-disable no-param-reassign */
import { createReducer } from '@reduxjs/toolkit';

import { v4 as uuidv4 } from 'uuid';
import {
  reset,
  updatePoint,
  setPoints,
  removePoint,
  addPoint,
  addOrUpdateEmergencyContact,
  removeSelectedEmergencyContact,
  setEmergencyContacts,
  setPlanIsLoggingOn,
  setContacts,
  setPlanHasDiving,
} from './actions';
import {
  PlanEmergencyContact,
  PlanEmergencyContacts,
  PlanPointEntry,
  PlanState,
} from './models';

const initialStartPointId = uuidv4();
const initialEndPointId = uuidv4();

export const initialStartPoint = {
  id: initialStartPointId,
  name: '',
  ordinal: 0,
};

export const initialEndPoint = {
  id: initialEndPointId,
  name: '',
  ordinal: 1,
};

export const initialState: PlanState = {
  selectedEmergencyContacts: {},
  emergencyContacts: {},
  planIsLoggingOn: undefined,
  isDiving: undefined,
  points: {
    [initialStartPointId]: initialStartPoint,
    [initialEndPointId]: initialEndPoint,
  },
};

/**
 * Converts an array of objects into an object where the keys are determined by a provided key function.
 *
 * @template T - The type of the objects in the array.
 * @template K - The type of the keys.
 * @param {T[]} array - The array of objects to convert.
 * @param {(item: T) => K} getKey - The function that determines the key for each object.
 * @returns {Record<K, T>} - The resulting object where the keys are determined by the key function.
 */
export const arrayToObject = <T extends Record<K, any>, K extends keyof any>(
  array: T[] = [],
  getKey: (item: T) => K,
) =>
  array.reduce((obj, cur) => {
    const key = getKey(cur);
    return { ...obj, [key]: cur };
  }, {} as Record<K, T>);

const recalculateOrdinals = (points: PlanPointEntry): PlanPointEntry => {
  const pointsArray = Object.values(points);

  const orderedPoints = pointsArray
    .filter((p) => p.ordinal >= 0)
    .sort((a, b) => (a.ordinal < b.ordinal ? -1 : 1));
  pointsArray
    .filter((p) => p.ordinal < 0)
    .forEach((p) => {
      // Add p to orderedPoints as the second last element
      orderedPoints.splice(orderedPoints.length - 1, 0, p);
    });

  const calculatedPoints = orderedPoints.map((point, index) => ({
    ...point,
    ordinal: index,
  }));
  return arrayToObject(calculatedPoints, (point) => point.id);
};

const buildEmergencyContactList = (
  contacts: PlanEmergencyContact[],
  state: PlanState,
) =>
  contacts.reduce((acc: PlanEmergencyContacts, item) => {
    const previousContact = state.emergencyContacts[item.id];

    acc[item.id] = {
      // Keep what was previously there for this contact
      ...previousContact,

      // Add additional properties from the new contact
      ...item,

      // Make sure there is always a contact type
      contactType:
        item?.contactType || previousContact?.contactType || 'onland',

      updatedAt:
        item?.updatedAt ||
        previousContact?.updatedAt ||
        new Date().toISOString(),
    };
    return acc;
  }, {});

// eslint-disable-next-line import/prefer-default-export
export const reducer = createReducer(initialState, (builder) => {
  builder.addCase(setContacts, (state, { payload }) => {
    state.emergencyContacts = buildEmergencyContactList(payload, state);
  });
  builder.addCase(setEmergencyContacts, (state, { payload }) => {
    state.emergencyContacts = buildEmergencyContactList(payload, state);
    state.selectedEmergencyContacts = state.emergencyContacts;
  });

  builder.addCase(addOrUpdateEmergencyContact, (state, { payload }) => {
    state.emergencyContacts = {
      ...state.emergencyContacts,
      [payload.id]: payload,
    };
    state.selectedEmergencyContacts = {
      ...state.selectedEmergencyContacts,
      [payload.id]: {
        ...state.selectedEmergencyContacts[payload.id],
        ...payload,
      },
    };
  });

  builder.addCase(removeSelectedEmergencyContact, (state, { payload }) => {
    delete state.selectedEmergencyContacts[payload];
  });

  builder.addCase(addPoint, (state, { payload }) => {
    const point = payload;

    const updatePoints = {
      ...state.points,
      [point.id]: { ...point, ordinal: -1 },
    };

    state.points = recalculateOrdinals(updatePoints);
  });
  builder.addCase(updatePoint, (state, { payload }) => {
    const { id, latitude, longitude, isRoundTrip, name } = payload;
    const previousPoint = state?.points[id];

    const newName = name === undefined ? previousPoint.name : name;

    let otherEnd;
    if (isRoundTrip) {
      if (id === initialStartPointId) {
        otherEnd = {
          [initialEndPointId]: {
            ...state?.points[initialEndPointId],
            latitude,
            longitude,
            name: newName,
          },
        };
      }
      if (id === initialEndPointId) {
        otherEnd = {
          [initialStartPointId]: {
            ...state?.points[initialStartPointId],
            latitude,
            longitude,
            name: newName,
          },
        };
      }
    }
    if (previousPoint) {
      const updatePoints = {
        ...state.points,
        [id]: {
          ...previousPoint,
          latitude,
          longitude,
          name: newName,
        },
        ...otherEnd,
      };
      state.points = recalculateOrdinals(updatePoints);
    }
  });
  builder.addCase(setPoints, (state, { payload }) => {
    state.points = recalculateOrdinals(
      arrayToObject(payload, (point) => point.id),
    );
  });
  builder.addCase(setPlanIsLoggingOn, (state, { payload }) => {
    state.planIsLoggingOn = payload;
  });
  builder.addCase(setPlanHasDiving, (state, { payload }) => {
    state.isDiving = payload;
  });
  builder.addCase(removePoint, (state, { payload }) => {
    delete state.points[payload];
    state.points = recalculateOrdinals(state.points);
  });
  builder.addCase(reset, (state) => {
    Object.assign(state, initialState);
  });
});
